import { IFreshdeskTicket } from "@/models/freshdeskTicket/freshdeskTicket.types"
import { JuniferComm } from "@/models/juniferCommunication/juniferComm"
import { IJuniferComm } from "@/models/juniferCommunication/juniferComm.types"
import { JuniferTicket } from "@/models/juniferTickets/juniferTicket"
import { IJuniferTicket } from "@/models/juniferTickets/juniferTicket.types"
import { Note } from "@/models/note/note"
import { INote } from "@/models/note/note.types"
import { ITransaction } from "@/models/transaction/transaction.types"
import { Dayjs } from "dayjs"
import { Status } from "@/types/Status"
import { getUtilityName, Utility } from "@/types/Utility"
import {
  SoSimpleTableHeaders,
  SoSimpleTableRow,
  SoSimpleTableRowType,
} from "@/types/components/SoSimpleTable"
import { RouteNames } from "@/types/router"
import { formatMoney } from "@/helpers/moneyHelpers"
import { ICommunicationsEmail } from "@/models/communicationsEmail/communicationsEmail.type"
import {
  IMeterAppointment,
  MeterAppointmentStatus,
} from "@/models/meterAppointment/meterAppointment.types"
import { getUserPermissions } from "@/services-handlers/getUserPermissions"
import { ICase } from "../case/case.type"
import { IComplaint } from "../complaint/complaint.types"
import { formatDate } from "@/helpers/dateHelper"
import { yesNo } from "@/helpers/stringHelpers"
import { ASSIGN_ERROR_CODE, DCA_CODES } from "@/constants/debtCollection"
import { AlertTypes } from "@/components/SoAlert.vue"

export enum CommItemType {
  Junifer = "junCommunication",
}

export interface IFeedItemAlert {
  type: AlertTypes
  title: string
  content: string
}

interface IFeedItemProps {
  icon: Utility
  title: string
  date: Dayjs
  status?: Status
  subtitle?: string
  chip?: string
  alert?: IFeedItemAlert
}

interface IDisplayInfo {
  headers: SoSimpleTableHeaders
  row: SoSimpleTableRow
  path?: string
  copyFields?: SoSimpleTableHeaders
}

export interface IFeedItem {
  readonly props: IFeedItemProps
  // Identifies the item across re-renders, so a list that changes around it
  // doesn't hand its expanded state to a different item.
  readonly feedKey: string
  info?: IDisplayInfo
  html?: string
  htmlPath?: string
}

interface FeedItemWithStatus {
  status: Status
}

const generateFeedItemProps = <T extends FeedItemWithStatus>(
  type: Utility,
  date: Dayjs,
  item?: T,
): IFeedItemProps => {
  return {
    icon: type,
    title: getUtilityName(type),
    date: date,
    ...(item && { status: item.status }),
  }
}

export class JuniferCommFeedItem implements IFeedItem {
  readonly feedKey: string
  item: JuniferComm
  type: Utility
  createdDate: Dayjs
  communicationType: string
  info: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: IJuniferComm) {
    this.feedKey = `junifer-comm-${item.id}`
    this.item = item
    this.type = Utility.Communication
    this.createdDate = item.createdDate
    this.communicationType = CommItemType.Junifer

    this.info = {
      headers: ["Type", "Attachments"],
      row: [this.item.type, this.item.files.length],
    }
  }

  get props(): IFeedItemProps {
    const regex = /\b Communication\b$/i
    const feedItemProps = generateFeedItemProps(this.type, this.createdDate)
    return {
      ...feedItemProps,
      title: `${feedItemProps.title} - ${this.item.type.replace(regex, "")}`,
    }
  }
}

export class JuniferTicketFeedItem implements IFeedItem {
  readonly feedKey: string
  item: JuniferTicket
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  dcaNames: string[]
  html?: string
  htmlPath?: string

  constructor(item: IJuniferTicket, dcaNames: string[] = []) {
    this.feedKey = `junifer-ticket-${item.id}`
    this.item = item
    this.type = Utility.Junifer
    this.createdDate = item.createdDate
    this.dcaNames = dcaNames

    this.info = {
      headers: [
        "Code",
        "Name",
        "Priority",
        "Step Code",
        "Identifier",
        "Summary",
        "Description",
      ],
      row: [
        this.item.code,
        this.item.name,
        this.item.priority,
        this.item.stepCode,
        this.item.identifier,
        this.item.summary,
        this.item.description || "N/A",
      ],
      path: RouteNames.CustomerJunifer,
    }
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(
      this.type,
      this.createdDate,
      this.item,
    )

    const isDebtRelated =
      DCA_CODES.includes(this.item.name) ||
      this.dcaNames.some((name) => this.item.name?.includes(name))

    const chip = isDebtRelated ? "DCA" : undefined

    const assignedToDca = this.item.summary?.includes("assigned to DCA")

    const isOpen = this.item.statusText === "Open"

    const dcaName =
      this.dcaNames.find((name) => this.item.name?.includes(name)) || ""

    const assignedInError = this.item.stepCode === ASSIGN_ERROR_CODE

    let alert: IFeedItemAlert | undefined

    if (assignedToDca && isOpen) {
      alert = {
        type: "info",
        title: "Confirm with the customer",
        content: `
          <ul class="ml-6">
            <li>Their debt has been passed to ${dcaName}, one of our Debt Collection Partners.</li>
            <li>This is because the account has been in debit for some time.</li>
            <li>${dcaName} will contact you to discuss the debt - please work with them to reach an agreement.</li>
          </ul>
        `,
      }
    } else if (assignedInError) {
      alert = {
        type: "warning",
        title: "Account assigned in error",
        content: `This means this account was placed with a Debt Collection Agency, but this was done in error, and the account was recalled. Please ignore this ticket.`,
      }
    }

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} Ticket - ${this.item.name}`,
      alert,
      chip,
    }
  }
}

export class NoteFeedItem implements IFeedItem {
  readonly feedKey: string
  item: Note
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  dcaNames: string[]
  html?: string
  htmlPath?: string

  constructor(item: INote, dcaNames: string[] = []) {
    this.feedKey = `note-${item.id}`
    this.item = item
    this.type = Utility.Note
    this.createdDate = item.createdDate
    this.dcaNames = dcaNames

    this.info = {
      headers: ["User", "Note Id", "Summary", "Content"],
      row: [this.item.user, this.item.id, this.item.summary, this.item.content],
    }
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(this.type, this.createdDate)

    const dcaName =
      this.dcaNames.find((name) => this.item.content?.includes(name)) || ""

    const chip = dcaName ? "DCA" : undefined

    const debtRecovered = this.item.content?.includes("Paid in Full")

    const alert: IFeedItemAlert | undefined = debtRecovered
      ? {
          type: "success",
          title: "Successful debt recovery",
          content: `Account returned to us by ${dcaName} - PIF - Paid in Full`,
        }
      : undefined

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} - ${this.item.summary}`,
      alert,
      chip,
    }
  }
}

export class FreshdeskTicketFeedItem implements IFeedItem {
  readonly feedKey: string
  item: IFreshdeskTicket
  type: Utility
  createdDate: Dayjs
  info?: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: IFreshdeskTicket) {
    this.feedKey = `freshdesk-${item.id}`
    this.item = item
    this.type = item.isComplaint ? Utility.Complaint : Utility.Freshdesk
    this.createdDate = item.createdDate
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(
      this.type,
      this.createdDate,
      this.item,
    )

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} - ${this.item.subject}`,
    }
  }
}

export class CaseFeedItem implements IFeedItem {
  readonly feedKey: string
  item: ICase
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: ICase) {
    this.feedKey = `case-${item.id}`
    this.item = item
    this.type = Utility.Case
    this.createdDate = item.createdAt

    this.info = {
      headers: ["Title", "Case reason"],
      row: [this.item.fields.title ?? "", this.item.fields.caseReason ?? ""],
      path: `${
        import.meta.env.SO_AWS_CONNECT_ORIGIN
      }/cases/agent-app/case-detail/${this.item.externalId}`,
    }
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(
      this.type,
      this.createdDate,
      this.item,
    )

    const assignedAgentOrNull =
      this.item.fields.assignedAgentName &&
      `Assigned to ${this.item.fields.assignedAgentName}`

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} - ${this.item.fields.referenceNumber}`,
      subtitle: assignedAgentOrNull,
    }
  }
}

export class ComplaintFeedItem implements IFeedItem {
  readonly feedKey: string
  item: IComplaint
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: IComplaint) {
    this.feedKey = `complaint-${item.id}`
    this.item = item
    this.type = Utility.Complaint
    this.createdDate = item.createdAt

    this.info = {
      headers: [
        "Complaint ID",
        "Category 1",
        "Category 2",
        "Category 3",
        "Closed at",
        "Goodwill amount",
        "D+1 letter",
        "8 week letter",
        "Recent comms sent",
        "Overdue comms sent",
        "Deadlock letter",
        "Internal category",
        "Ofgem category",
        "Ombudsman case",
        "Channel",
        "Source",
        "External ID",
        "Status",
        "Sub status",
        "Type",
      ],
      row: [
        this.item.complaintId ?? "-",
        this.item.category1 ?? "-",
        this.item.category2 ?? "-",
        this.item.category3 ?? "-",
        formatDate(this.item.closedAt) || "-",
        this.item.complaintsGoodwillAmount ?? "-",
        formatDate(this.item.sendDateOfD1SignpostingComms) || "-",
        formatDate(this.item.sendDateOfD56SignpostingComms) || "-",
        formatDate(this.item.sendDateOfRecentSignpostingComms) || "-",
        formatDate(this.item.sendDateOfOverdueSignpostingComms) || "-",
        yesNo(this.item.deadlockCommsSent),
        this.item.internalComplaintCategory ?? "-",
        this.item.ofgemMappedComplaintCategory ?? "-",
        this.item.ombudsmanCase ?? "-",
        this.item.channel ?? "-",
        this.item.source ?? "-",
        this.item.externalIdentifier ?? "-",
        this.item.statusText ?? "-",
        this.item.subStatus ?? "-",
        this.item.type ?? "-",
      ],
      path: this.item.externalIdentifierPath?.props.url ?? "",
    }
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(
      this.type,
      this.createdDate,
      this.item,
    )

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} ${
        this.item.category1 ? "- " + this.item.category1 : ""
      }`,
    }
  }
}

export class TransactionFeedItem implements IFeedItem {
  readonly feedKey: string
  item: ITransaction
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: ITransaction) {
    this.feedKey = `transaction-${item.id}`
    this.item = item
    if (item.isBill) {
      this.type = Utility.Bill
      this.info = {
        headers: ["Payment amount", "Account balance", "Reference"],
        row: [
          this.item.changeAmount || "-",
          this.item.accountBalance || "-",
          this.item.reference,
        ],
      }
    } else if (item.isPayment) {
      this.type = Utility.Payment
      this.info = {
        headers: [
          "Payment amount",
          "Account balance",
          "Description",
          "Reference",
        ],
        row: [
          this.item.changeAmount || "-",
          this.item.accountBalance || "-",
          this.item.description ?? "-",
          this.item.reference,
        ],
      }
    } else {
      this.type = Utility.Other
      this.info = {
        headers: ["Type", "Change amount", "Description", "Reference"],
        row: [
          this.item.transactionType,
          this.item.changeAmount || "-",
          this.item.description ?? "-",
          this.item.reference,
        ],
      }
    }
    this.createdDate = item.acceptedDttm || item.createdDttm
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(this.type, this.createdDate)

    const chip = this.item.reference?.includes("Payment to DCA")
      ? "DCA"
      : undefined

    return {
      ...feedItemProps,
      title: `${feedItemProps.title} - ${formatMoney({
        amount: this.item.changeAmount,
      })}`,
      chip,
    }
  }
}

export class CommEmailFeedItem implements IFeedItem {
  readonly feedKey: string
  item: ICommunicationsEmail
  type: Utility
  createdDate: Dayjs
  info?: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: ICommunicationsEmail) {
    this.feedKey = `comm-email-${item.id}`
    this.item = item
    this.type = Utility.Email
    this.createdDate = item.dateDelivered
    this.html = item.html
    this.htmlPath = item.htmlPath
  }

  get props(): IFeedItemProps {
    const feedItemProps = generateFeedItemProps(this.type, this.createdDate)
    return {
      ...feedItemProps,
      title: "Email - " + this.item.subject,
      icon: Utility.Communication,
    }
  }
}

export class MeterAppointmentFeedItem implements IFeedItem {
  readonly feedKey: string
  item: IMeterAppointment
  type: Utility
  createdDate: Dayjs
  info: IDisplayInfo
  html?: string
  htmlPath?: string

  constructor(item: IMeterAppointment) {
    this.feedKey = `appointment-${item.id}`
    const isAborted = item.aesStatus === MeterAppointmentStatus.ABORTED
    const isCancelled = item.aesStatus === MeterAppointmentStatus.CANCELLED
    const getAbortAnswer = (question: string) =>
      item.parsedAbortReason.find((entry) => entry.question === question)
        ?.answer || "-"
    const abortImage = item.abortImageFromSurvey
      ? {
          image: item.abortImageFromSurvey,
          type: SoSimpleTableRowType.LINK,
          text: "See photo",
        }
      : "-"

    this.item = item
    this.type = Utility.Appointment
    this.createdDate = item.createdAt
    this.info = {
      headers: [
        ...(isAborted
          ? [
              "Abort reason",
              "Abort notes",
              "Abort comments",
              "Abort image",
              "Any network reported issues? If Yes, select the DNO Ref Number",
              "DNO Ref Number",
              "Next steps",
            ]
          : []),
        ...(isCancelled ? ["Cancellation reason"] : []),
        "Site visit slot",
        "Installer",
        "Engineer",
        "Appointment ID",
        "Booking reference",
        "Target meter",
        "Appointment type",
        "Meter type",
        "MPAN",
        "MPRN",
        "Initial Elec MSN",
        "Initial Gas MSN",
        ...item.removedMeterReads.map((read) => read.question),
      ],
      row: [
        ...(isAborted
          ? [
              getAbortAnswer("Abort reason"),
              getAbortAnswer("Abort notes"),
              getAbortAnswer("Comments"),
              abortImage,
              getAbortAnswer(
                "Any network reported issues? If Yes, select the DNO Ref Number",
              ),
              getAbortAnswer("DNO Ref Number"),
              "-",
            ]
          : []),
        ...(isCancelled ? [item.cancelReason || "-"] : []),
        this.item.timeSlot || this.item.timeSlotRange,
        this.item.installer,
        getUserPermissions.hasPermission("agent.customer.showEngineerDetails")
          ? this.item.engineerDetails || "-"
          : "*Redacted*",
        this.item.appointmentId || "-",
        this.item.displayJobNumber,
        this.item.installationType || "-",
        this.item.appointmentType || "-",
        this.item.meterType || "-",
        this.item.mpan || "-",
        this.item.mprn || "-",
        this.item.electricMeterSerialNumber || "-",
        this.item.gasMeterSerialNumber || "-",
        ...item.removedMeterReads.map((read) => read.answer || "-"),
      ],
      copyFields: ["Appointment ID", "Booking reference"],
    }
  }

  get props(): IFeedItemProps {
    return {
      ...generateFeedItemProps(this.type, this.createdDate, this.item),
      title: this.item.title,
    }
  }
}

export type ActivityFeedItemType =
  | JuniferCommFeedItem
  | FreshdeskTicketFeedItem
  | CaseFeedItem
  | ComplaintFeedItem
  | JuniferTicketFeedItem
  | TransactionFeedItem
  | NoteFeedItem
  | CommEmailFeedItem
  | MeterAppointmentFeedItem

export interface IItemsByDate {
  date: string
  items: ActivityFeedItemType[]
}
